import React, { useEffect, useMemo, useRef } from "react";
import {
  ProductListEditor,
  UserInsertField,
  calculateGraph,
} from "./module-builder-surface";
import {
  authorProductListFromNode,
  writeProductListToNode,
} from "../utils/product-list";
import { recommendedInsertGroups } from "./nested-module-insert-fields-core";
import { parseMoneyAmount } from "../utils";
import {
  collectRecommendedInsertTree,
  flattenNestedCalculationTree,
} from "../utils/nested-calculation-tree";

/**
 * Day-to-day Edit calc panels (products + notes).
 * Built on shared module primitives (`ProductListEditor`, `UserInsertField`).
 * Product-list editing lives only under Produkter og felter — Anbefalte felter
 * never mounts ProductListEditor; product catalogs get a compact jump row.
 * If the standalone calc page gains the same tabs, reuse these — do not fork.
 *
 * Consumers: `nested-calculation-editor-dialog.js`
 * Shared surface rule: `.cursor/rules/offer-module-builder-sync.mdc`
 */

function catalogOptionCount(node) {
  return authorProductListFromNode(node).options.length;
}

function ProductListPointerRow({ node, t, onJumpToProducts }) {
  const title =
    node.title || t("offer.calculation.module.node.product_catalog");
  const count = catalogOptionCount(node);
  return (
    <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-sm">
      <div className="min-w-0 truncate text-slate-700">
        <span className="font-medium text-slate-800">{title}</span>
        <span className="text-slate-500">
          {" · "}
          {t("offer.calculation.module.meta.product_lines", { count })}
        </span>
      </div>
      {typeof onJumpToProducts === "function" ? (
        <button
          type="button"
          className="shrink-0 text-xs font-semibold text-[#1463C7] hover:underline"
          onClick={onJumpToProducts}
        >
          {t("offer.calculation.line.products_jump")}
        </button>
      ) : (
        <span className="shrink-0 text-xs text-slate-500">
          {t("offer.calculation.line.products_in_builder")}
        </span>
      )}
    </div>
  );
}

/**
 * Dense recommended-field rows. Product catalogs → summary + jump only
 * (full ProductListEditor stays in the products section above).
 */
function renderRecommendedGroups({
  groups,
  t,
  updateNodeField,
  onJumpToProducts,
}) {
  const fieldGroups = groups.filter((g) => g.fields.length > 0);
  const productPointers = groups.filter(
    (g) => g.productsUnlocked && g.fields.length === 0
  );
  if (!fieldGroups.length && !productPointers.length) return null;

  return (
    <div className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white">
      {fieldGroups.map(({ node, fields, productsUnlocked }) => (
        <div key={node.id} className="px-2.5 py-2">
          <div className="mb-1 flex items-center justify-between gap-2">
            <div className="truncate text-xs font-semibold text-slate-600">
              {node.title || node.type}
            </div>
            {productsUnlocked && typeof onJumpToProducts === "function" ? (
              <button
                type="button"
                className="shrink-0 text-xs font-semibold text-[#1463C7] hover:underline"
                onClick={onJumpToProducts}
              >
                {t("offer.calculation.line.products_jump")}
              </button>
            ) : null}
          </div>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {fields.map((field) => (
              <UserInsertField
                field={field}
                key={field.key}
                locked={false}
                value={
                  field.key === "enabled"
                    ? node.data?.[field.key] !== false
                    : node.data?.[field.key] ?? ""
                }
                onChange={(value) => updateNodeField(node.id, field.key, value)}
                t={t}
              />
            ))}
          </div>
        </div>
      ))}
      {productPointers.map(({ node }) => (
        <ProductListPointerRow
          key={node.id}
          node={node}
          t={t}
          onJumpToProducts={onJumpToProducts}
        />
      ))}
    </div>
  );
}

/**
 * Primary day-to-day panel: product catalogs + unlocked recommended fields
 * for this calc and every nested descendant (multi-level mal-import).
 */
export function NestedCalcProductsPanel({
  formik,
  t,
  focusRecommended = false,
  dialogZIndex = 4300,
  treeRoot = null,
  onPatchDescendant = null,
  onOpenDescendant = null,
}) {
  const graph = formik.values?.module_graph || { nodes: [], edges: [] };
  const nodes = Array.isArray(graph.nodes) ? graph.nodes : [];
  const recommendedRef = useRef(null);
  const productsRef = useRef(null);
  const catalogNodes = useMemo(
    () => nodes.filter((n) => n?.type === "productCatalog"),
    [nodes]
  );
  const rootId = formik.values?.id;
  const recommended = useMemo(
    () => recommendedInsertGroups(graph, t),
    [graph, t]
  );
  const treeSource = treeRoot || formik.values;
  const treeRecommended = useMemo(
    () =>
      collectRecommendedInsertTree(treeSource, t).filter(
        (row) => String(row.calc?.id) !== String(rootId)
      ),
    [treeSource, t, rootId]
  );
  const structureRows = useMemo(
    () =>
      flattenNestedCalculationTree(treeSource).filter(
        (row) => String(row.calc?.id) !== String(rootId)
      ),
    [treeSource, rootId]
  );
  const total = parseMoneyAmount(
    formik.values?.module_result?.total ?? formik.values?.total_amount
  );

  useEffect(() => {
    if (!focusRecommended) return undefined;
    const id = window.setTimeout(() => {
      const el = recommendedRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    }, 80);
    return () => window.clearTimeout(id);
  }, [focusRecommended]);

  const patchNode = (nodeId, nextNode) => {
    const nextNodes = nodes.map((n) => (n.id === nodeId ? nextNode : n));
    const nextGraph = { ...graph, nodes: nextNodes };
    formik.setFieldValue("module_graph", nextGraph);
    formik.setFieldValue("module_enabled", true);
    // Live preview: recommended qty/fields must update totals immediately.
    try {
      const preview = calculateGraph(nextGraph, { safeMode: true });
      formik.setFieldValue("module_result", {
        ...preview,
        calculated_at: new Date().toISOString(),
        server_evaluated: false,
      });
    } catch {
      /* keep prior module_result */
    }
  };

  const updateNodeField = (nodeId, key, value) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    patchNode(nodeId, {
      ...node,
      data: {
        ...(node.data || {}),
        [key]: key === "enabled" ? value === true || value === "true" : value,
      },
    });
  };

  const updateDescendantNodeField = (calcId, nodeId, key, value) => {
    if (typeof onPatchDescendant !== "function") return;
    onPatchDescendant(calcId, (calc) => {
      const g = calc?.module_graph || { nodes: [], edges: [] };
      const gNodes = Array.isArray(g.nodes) ? g.nodes : [];
      const node = gNodes.find((n) => n.id === nodeId);
      if (!node) return calc;
      const nextGraph = {
        ...g,
        nodes: gNodes.map((n) =>
          n.id === nodeId
            ? {
                ...n,
                data: {
                  ...(n.data || {}),
                  [key]:
                    key === "enabled"
                      ? value === true || value === "true"
                      : value,
                },
              }
            : n
        ),
      };
      let moduleResult = calc.module_result;
      try {
        moduleResult = {
          ...calculateGraph(nextGraph, { safeMode: true }),
          calculated_at: new Date().toISOString(),
          server_evaluated: false,
        };
      } catch {
        /* keep */
      }
      return {
        ...calc,
        module_enabled: true,
        module_graph: nextGraph,
        module_result: moduleResult,
      };
    });
  };

  const markInsertConfirmed = () => {
    formik.setFieldValue("module_graph", {
      ...graph,
      meta: {
        ...(graph.meta || {}),
        insert_confirmed_at: new Date().toISOString(),
      },
    });
    treeRecommended.forEach((row) => {
      if (!row.calc?.id || typeof onPatchDescendant !== "function") return;
      onPatchDescendant(row.calc.id, (calc) => {
        const g = calc?.module_graph || {};
        return {
          ...calc,
          module_graph: {
            ...g,
            meta: {
              ...(g.meta || {}),
              insert_confirmed_at: new Date().toISOString(),
            },
          },
        };
      });
    });
  };

  const hasAnyRecommended =
    recommended.length > 0 || treeRecommended.length > 0;

  const jumpToProducts = () => {
    const el = productsRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  };

  return (
    <div className="space-y-5">
      <div className="nested-calc-editor__unit-total">
        <div className="nested-calc-editor__unit-total-label">
          {t("offers.calculation.nested.unit_total_label")}
        </div>
        <div className="nested-calc-editor__unit-total-value">
          {total != null
            ? new Intl.NumberFormat("nb-NO", {
                style: "currency",
                currency: "NOK",
              }).format(total)
            : "—"}
        </div>
        <p className="nested-calc-editor__unit-total-hint">
          {t("offers.calculation.nested.unit_total_hint")}
        </p>
      </div>

      <section ref={productsRef} className="scroll-mt-3">
        <h3 className="mb-1 text-sm font-semibold text-slate-900">
          {t("offer.calculation.product_list.title")}
        </h3>
        <p className="mb-3 text-xs text-slate-500">
          {t("offers.calculation.nested.products_panel_hint")}
        </p>
        {catalogNodes.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
            {t("offers.calculation.nested.no_product_catalogs")}
          </p>
        ) : (
          <div className="space-y-4">
            {catalogNodes.map((node) => (
              <div key={node.id} className="nested-calc-editor__section-card">
                <div className="mb-2 text-sm font-semibold text-slate-800">
                  {node.title || t("offer.calculation.module.node.product_catalog")}
                </div>
                <ProductListEditor
                  value={authorProductListFromNode(node)}
                  dialogZIndex={dialogZIndex}
                  onChange={(nextList) => {
                    const patched = writeProductListToNode(node, nextList);
                    patchNode(node.id, patched);
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      <section
        ref={recommendedRef}
        className={
          focusRecommended
            ? "rounded-lg border border-[#1463C7] bg-[#EAF3FD]/px-2.5 py-2 scroll-mt-3"
            : undefined
        }
      >
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900">
            {t("offer.calculation.line.insert_title")}
          </h3>
          {hasAnyRecommended ? (
            <button
              type="button"
              className="text-xs font-semibold text-[#1463C7] hover:underline"
              onClick={markInsertConfirmed}
            >
              {t("offer.calculation.line.insert_done")}
            </button>
          ) : null}
        </div>
        {!hasAnyRecommended ? (
          <p className="text-xs text-slate-500">
            {t("offer.calculation.line.insert_locked")}
          </p>
        ) : (
          <div className="space-y-2.5">
            {recommended.length > 0 ? (
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {formik.values?.title ||
                    t("offers.calculation.nested.this_calculation")}
                </p>
                {renderRecommendedGroups({
                  groups: recommended,
                  t,
                  updateNodeField,
                  onJumpToProducts: jumpToProducts,
                })}
              </div>
            ) : null}
            {treeRecommended.map((row) => (
              <div key={row.calc.id}>
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {row.pathTitles.join(" / ")}
                  </p>
                  {typeof onOpenDescendant === "function" ? (
                    <button
                      type="button"
                      className="text-xs font-semibold text-[#1463C7] hover:underline"
                      onClick={() => onOpenDescendant(row.calc)}
                    >
                      {t("offers.calculation.nested.edit_structure")}
                    </button>
                  ) : null}
                </div>
                {renderRecommendedGroups({
                  groups: row.groups,
                  t,
                  updateNodeField: (nodeId, key, value) =>
                    updateDescendantNodeField(row.calc.id, nodeId, key, value),
                  onJumpToProducts:
                    typeof onOpenDescendant === "function"
                      ? () => onOpenDescendant(row.calc)
                      : jumpToProducts,
                })}
              </div>
            ))}
          </div>
        )}
      </section>

      {structureRows.length > 0 ? (
        <section>
          <h3 className="mb-1 text-sm font-semibold text-slate-900">
            {t("offers.calculation.nested.structure_heading")}
          </h3>
          <p className="mb-3 text-xs text-slate-500">
            {t("offers.calculation.nested.structure_hint")}
          </p>
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
            {structureRows.map((row) => (
              <li
                key={row.calc.id || row.pathTitles.join("/")}
                className="flex items-center justify-between gap-3 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-800">
                    {row.calc.title || `#${row.calc.id}`}
                  </div>
                  <div className="truncate text-xs text-slate-500">
                    {row.pathTitles.join(" / ")}
                  </div>
                </div>
                {typeof onOpenDescendant === "function" ? (
                  <button
                    type="button"
                    className="shrink-0 text-xs font-semibold text-[#1463C7] hover:underline"
                    onClick={() => onOpenDescendant(row.calc)}
                  >
                    {t("offers.calculation.nested.edit_structure")}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/**
 * Module note nodes + kundetekst for offer/PDF (show_in_offer + node order).
 */
export function NestedCalcNotesPanel({ formik, t }) {
  const graph = formik.values?.module_graph || { nodes: [], edges: [] };
  const nodes = Array.isArray(graph.nodes) ? graph.nodes : [];
  const noteNodes = useMemo(
    () => nodes.filter((n) => n?.type === "note"),
    [nodes]
  );

  const setGraph = (nextGraph) => {
    formik.setFieldValue("module_graph", nextGraph);
    formik.setFieldValue("module_enabled", true);
  };

  const patchNode = (nodeId, nextNode) => {
    setGraph({
      ...graph,
      nodes: nodes.map((n) => (n.id === nodeId ? nextNode : n)),
    });
  };

  const updateNoteTitle = (nodeId, title) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    patchNode(nodeId, { ...node, title });
  };

  const updateNoteData = (nodeId, key, value) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    patchNode(nodeId, {
      ...node,
      data: {
        ...(node.data || {}),
        [key]: value,
      },
    });
  };

  const moveNote = (nodeId, direction) => {
    const noteIds = noteNodes.map((n) => n.id);
    const noteIndex = noteIds.indexOf(nodeId);
    if (noteIndex < 0) return;
    const swapWith = noteIndex + direction;
    if (swapWith < 0 || swapWith >= noteIds.length) return;

    const aId = noteIds[noteIndex];
    const bId = noteIds[swapWith];
    const aPos = nodes.findIndex((n) => n.id === aId);
    const bPos = nodes.findIndex((n) => n.id === bId);
    if (aPos < 0 || bPos < 0) return;

    const nextNodes = [...nodes];
    const tmp = nextNodes[aPos];
    nextNodes[aPos] = nextNodes[bPos];
    nextNodes[bPos] = tmp;
    setGraph({ ...graph, nodes: nextNodes });
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        {t("offers.calculation.nested.notes_panel_hint")}
      </p>

      <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <h3 className="mb-1 text-sm font-semibold text-slate-900">
          {t("offer.calculation.module.offer_text.customer_note")}
        </h3>
        <p className="mb-2 text-xs text-slate-500">
          {t("offers.calculation.nested.notes_offer_text_hint")}
        </p>
        <textarea
          className="min-h-[88px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]"
          value={graph.offer_text || ""}
          onChange={(e) =>
            setGraph({
              ...graph,
              offer_text: e.target.value,
            })
          }
          placeholder={t("offers.calculation.nested.notes_body_placeholder")}
        />
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-slate-900">
          {t("offers.calculation.nested.notes_heading")}
        </h3>
        {noteNodes.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
            {t("offers.calculation.nested.notes_empty")}
          </p>
        ) : (
          <div className="space-y-4">
            {noteNodes.map((node, index) => {
              const showInOffer = node.data?.show_in_offer !== false;
              return (
                <div
                  key={node.id}
                  className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t("offers.calculation.nested.notes_item_label", {
                        n: index + 1,
                      })}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="rounded border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                        disabled={index === 0}
                        onClick={() => moveNote(node.id, -1)}
                        title={t("offers.documentBuilder.move_up")}
                      >
                        {t("offers.documentBuilder.move_up")}
                      </button>
                      <button
                        type="button"
                        className="rounded border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                        disabled={index === noteNodes.length - 1}
                        onClick={() => moveNote(node.id, 1)}
                        title={t("offers.documentBuilder.move_down")}
                      >
                        {t("offers.documentBuilder.move_down")}
                      </button>
                    </div>
                  </div>

                  <label className="mb-3 grid gap-1 text-sm">
                    <span className="font-medium text-slate-700">
                      {t("offer.calculation.module.field.title")}
                    </span>
                    <input
                      type="text"
                      className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]"
                      value={node.title || ""}
                      onChange={(e) => updateNoteTitle(node.id, e.target.value)}
                      placeholder={t("offer.calculation.module.node.note")}
                    />
                  </label>

                  <label className="mb-3 grid gap-1 text-sm">
                    <span className="font-medium text-slate-700">
                      {t("offer.calculation.module.field.note")}
                    </span>
                    <textarea
                      className="min-h-[120px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]"
                      value={node.data?.note || ""}
                      onChange={(e) =>
                        updateNoteData(node.id, "note", e.target.value)
                      }
                      placeholder={t(
                        "offers.calculation.nested.notes_body_placeholder"
                      )}
                    />
                  </label>

                  <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
                      checked={showInOffer}
                      onChange={(e) =>
                        updateNoteData(
                          node.id,
                          "show_in_offer",
                          e.target.checked
                        )
                      }
                    />
                    <span>
                      <span className="font-medium">
                        {t("offer.calculation.line_editor.show_in_offer")}
                      </span>
                      <span className="mt-0.5 block text-xs text-slate-500">
                        {t("offers.calculation.nested.notes_show_in_offer_hint")}
                      </span>
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
